import React from 'react';
import { useLocation } from 'react-router-dom';
import { Trans, t } from '@lingui/macro';
import { Button, Dialog } from '@economic/taco';

import { PartialInvoiceDto } from '~/types/internal-api';
import PartialInvoicesTable from './PartialInvoicesTable';
import PartialInvoicingBalanceInfo from './PartialInvoicingBalanceInfo';
import CreatePartialInvoiceForm from './CreatePartialInvoiceForm';
import { SalesDocument } from '../../types';

interface Props {
    order: SalesDocument;
    partialInvoices: PartialInvoiceDto[];
    close: () => void;
    handleShowConvertToInvoiceConfirmation: () => void;
}

export default function CreatePartialInvoice({ order, partialInvoices, close, handleShowConvertToInvoiceConfirmation }: Props) {
    const location = useLocation();

    const salesDocumentId = location.state?.draftId;
    const openedFromOrder = location.pathname.includes('/orders');

    const partialInvoicesAmountSum = partialInvoices.reduce((sum, el) => sum + el.amount, 0);
    const orderRemainingAmount = order.amount - partialInvoicesAmountSum;

    return (
        <>
            <Dialog.Title className="!text-left mb-6">
                <Trans>Partial invoice</Trans>
            </Dialog.Title>
            {partialInvoices.length > 0 && (
                <>
                    <strong>
                        {openedFromOrder ? (
                            <Trans>Existing partial invoices on this order</Trans>
                        ) : (
                            <Trans>Existing partial invoices on order no. {order.number}</Trans>
                        )}
                    </strong>
                    <div className={partialInvoices.length > 3 ? 'h-56' : ''}>
                        <PartialInvoicesTable data={partialInvoices} />
                    </div>
                    <PartialInvoicingBalanceInfo
                        textGroups={[
                            { label: t`Total order amount`, value: order.amount },
                            { label: t`Partial invoices sum`, value: partialInvoicesAmountSum },
                            { label: t`Remaining amount`, value: orderRemainingAmount },
                        ]}
                    />
                </>
            )}

            {!openedFromOrder && (
                <Dialog.Footer>
                    <Button appearance="primary" onClick={close}>
                        <Trans>OK</Trans>
                    </Button>
                </Dialog.Footer>
            )}

            {openedFromOrder && (
                <h3 className="mt-6 mb-0">
                    <Trans>Create new partial invoice</Trans>
                </h3>
            )}

            {openedFromOrder && (
                <CreatePartialInvoiceForm
                    close={close}
                    orderId={salesDocumentId}
                    orderAmount={order.amount}
                    orderRemainingAmount={orderRemainingAmount}
                    handleShowConvertToInvoiceConfirmation={handleShowConvertToInvoiceConfirmation}
                    hasDraftPartialInvoices={partialInvoices.filter(el => !el.booked).length > 0}
                />
            )}
        </>
    );
}
